import React, { Dispatch, SetStateAction, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { InvoiceDA } from '../../services/admin/invoice.service';
import { Error } from '../../models/error.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Invoice } from '../../models/invoice.model';
import { legendInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendOptionalIcon, evaluateLegendRequired } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';
import AppLoading from '../Loading';

export interface AppModalInvoiceCreateReminderProps {
  id_company: string,
  invoice: Invoice | null,
  setInvoice: Dispatch<SetStateAction<Invoice | null>>
};

let errorResponse: Error, invoiceResponse: Invoice;

const AppModalInvoiceCreateReminder: React.FunctionComponent<AppModalInvoiceCreateReminderProps> = ({id_company, invoice, setInvoice}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [description, setDescription] = useState({value: '', valid: false})
  const [amount, setAmount] = useState({value: '', valid: true})

  const submitInvoiceCreateReminder = async () => {
    setLoadIndicator('on')

    if (invoice && description.valid && amount.valid) {
      await InvoiceDA.postInvoiceReminder(id_company, invoice.id, description.value, amount.value).then( (response) => {
        if (response.status === 201) {
          invoiceResponse = response.data
          setInvoice(invoiceResponse)

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedReminder,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalInvoiceCreateReminder()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!description.value.length) legendInvalid('modal-invoice-create-reminder-legend-description-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeDescription = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setDescription({...description, value: event.target.value})
  }

  const handleChangeAmount = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setAmount({...amount, value: event.target.value})
  }

  const validateDescription = () => {
    evaluateLegendRequired(expressions.text, description, setDescription, 'modal-invoice-create-reminder-legend-description-valid', 'modal-invoice-create-reminder-legend-description-required')
  }

  const validateAmount = () => {
    evaluateLegendOptionalIcon(expressions.currency, amount, setAmount, 'modal-invoice-create-reminder-input-amount', 'modal-invoice-create-reminder-legend-amount-valid')
  }

  const executeHideModalInvoiceCreateReminder = () => {
    modalHide('modal-invoice-create-reminder')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-invoice-create-reminder-legend-description-valid')
    legendValidInvalidRestart('modal-invoice-create-reminder-legend-description-required')
    legendIconValidInvalidRestart('modal-invoice-create-reminder-input-amount', 'modal-invoice-create-reminder-legend-amount-valid')

    setDescription({value: '', valid: true})
    setAmount({value: '', valid: false})
  }

  return (
    <div id="modal-invoice-create-reminder" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.createReminder}</h2>
          </div>
          <div className="modal-body">
            { invoice
              ?
              <div className="form">
                <div className="fv-row mb-3">
                  <label className="form-label required">{lang.labels.description}</label>
                  <TextareaAutosize className="form-control" name="description" minRows={2} maxRows={3} value={description.value} onChange={handleChangeDescription} onKeyUp={validateDescription} onBlur={validateDescription} />
                  <AppLegend component={"modal-invoice-create-reminder"}  attribute={{check: description.valid, name: "description", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
                <div className="fv-row">
                  <label className="form-label">{lang.labels.amount}</label>
                  <div className="position-relative">
                    <div className="position-absolute translate-middle-y top-50 start-0 fw-bolder ms-5">{invoice.currency.symbol}</div>  
                    <input id="modal-invoice-create-reminder-input-amount" className="form-control ps-20" type="text" autoComplete="off" name="amount" placeholder="0.00" value={amount.value} onChange={handleChangeAmount} onKeyUp={validateAmount} onBlur={validateAmount} />
                  </div>
                  <div className="form-text text-justify">{lang.labels.ifNoAmountIsEnteredTheValueWillBeZero}</div>
                  <AppLegend component={"modal-invoice-create-reminder"}  attribute={{check: amount.valid, name: "amount", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
              :
              <AppLoading></AppLoading>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalInvoiceCreateReminder}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitInvoiceCreateReminder}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalInvoiceCreateReminder;
